<template>
    <div>
        <div class="product-adder-container">
            <!-- Single Row Layout - All Fields Horizontal -->
            <div class="fields-row">
                <!-- Product Search Field -->
                <div class="field-group field-group-product">
                    <label class="field-label">Product*</label>
                    <ProductSearchInput 
                        :defaultProductId="defaultProductId" 
                        :warehouseIdFilter="selectedWarehouse || ''"
                        :roomFrontFilter="fromFrontRoom"
                        ref="searchInput" 
                        @change="selectedProduct = $event" 
                    />
                </div>

                <!-- Location Dropdown -->
                <template v-if="!onlyProductInput">
                    <div class="field-group" v-if="!lockWarehouse">
                        <label class="field-label">Location*</label>
                        <SelectDropdown
                            v-if="filteredWarehouses.length"
                            :options="filteredWarehouses"
                            :value="selectedWarehouse"
                            @change="selectedWarehouse = $event"
                        />
                        <div class="field-empty-state" v-else>No Warehouses Found</div>
                    </div>

                    <!-- Room Dropdown -->
                    <div class="field-group" v-if="selectedWarehouse && !lockRoom">
                        <label class="field-label">Room*</label>
                        <SelectDropdown
                            v-if="filteredRooms.length"
                            :options="filteredRooms"
                            :value="selectedRoom"
                            @change="selectedRoom = $event"
                        />
                        <div class="field-empty-state" v-else>No Rooms Found</div>
                    </div>

                    <!-- Package Dropdown -->
                    <div class="field-group field-group-package" v-if="selectedRoom && selectedProduct">
                        <label class="field-label">Package #*</label>
                        <SelectDropdown
                            v-if="filteredPackages.length"
                            :value="selectedPackage"
                            :options="filteredPackages"
                            @change="selectedPackage = $event"
                        />
                        <div class="field-empty-state" v-else>No Packages Found</div>
                    </div>

                    <!-- Quantity Input -->
                    <div class="field-group field-group-qty" v-if="selectedRoom && selectedProduct">
                        <label class="field-label">Qty*</label>
                        <input 
                            type="number" 
                            class="qty-input" 
                            v-model.number="transferQty" 
                            :min="1" 
                            :max="availableMaxQty" 
                            step="1" 
                            @input="clampQty"
                        />
                        <div class="qty-hint" v-if="availableMaxQty !== null">Max: {{ availableMaxQty }}</div>
                    </div>

                    <!-- Add Button -->
                    <div class="field-group field-group-action" v-if="selectedProduct">
                        <label class="field-label">&nbsp;</label>
                         <Button 
                            :disabled="!allowAddProduct" 
                            noMargin 
                            @click="addProduct"
                            class="add-product-btn"
                        >
                            <i class="fas fa-plus"></i> Add
                        </Button>
                    </div>
                </template>

                <!-- Product-only workflows still need an explicit way to add the selection. -->
                <div class="field-group field-group-action" v-else-if="selectedProduct">
                    <label class="field-label">&nbsp;</label>
                    <Button
                        :disabled="!allowAddProduct"
                        noMargin
                        @click="addProduct"
                        class="add-product-btn"
                    >
                        <i class="fas fa-plus"></i> Add
                    </Button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from "vuex"
import SelectDropdown from "../crud/SelectDropdown.vue"
import Button from "../UI/Button.vue"
import ProductSearchInput from "./ProductSearchInput.vue"

export default {
    data() {
        return {
            selectedProduct: null,
            selectedWarehouse: null,
            selectedRoom: null,
            selectedPackage: null,
            transferQty: 1,
        }
    },
    components: { Button, SelectDropdown, ProductSearchInput },
    computed: {
        ...mapState("misc", ["warehouses", "warehouseRooms"]),
        allowAddProduct() {
            return this.onlyProductInput ? this.selectedProduct && this.selectedProduct.id : this.selectedPackage
        },
        filteredWarehouses() {
            // Prefer warehouses where the selected product has stock; fallback to all
            if (this.selectedProduct) {
                const withStock = this.selectedProduct.warehouseEntries
                    .filter((we) => Number(we.stock_value) > 0 || Number(we.stock_value_back_room) > 0)
                    .map((item) => ({ id: item.warehouse.id, title: item.warehouse.name, crsaNo: item.warehouse.crsa_number }))
                if (withStock.length) return withStock
            }
            return this.warehouses.map((item) => ({ id: item.id, title: item.name, crsaNo: item.crsa_number }))
        },
        ledgerEntries() {
            return this.selectedProduct
                ? this.selectedProduct.ledgerEntries.filter((item) => {
                      return item.warehouse == this.selectedWarehouse
                  })
                : []
        },
        filteredRooms() {
            return Object.values(this.warehouseRooms)
        },
        filteredPackages() {
            // allow even 0 stock items to be added
            const res = this.ledgerEntries
                .filter((item) => (this.fromFrontRoom ? Number(item.stock_value) > 0 : Number(item.stock_value_back_room) > 0))
                .map((item) => ({
                    id: item.batch_no,
                    title: `${item.batch_no} (${this.fromFrontRoom ? item.stock_value : item.stock_value_back_room})`,
                }))
            return res
        },
        selectedRoomData() {
            return this.filteredRooms.find((item) => item.id == this.selectedRoom)
        },
        fromFrontRoom() {
            if (this.selectedRoomData) {
                const fromFrontRoom = this.selectedRoomData.id == this.warehouseRooms.FRONT_ROOM.id
                return fromFrontRoom
            }
            return false
        },
        availableMaxQty() {
            const le = this.selectedProduct && this.selectedPackage
                ? this.selectedProduct.ledgerEntries.find(
                    (item) => item && item.warehouse == this.selectedWarehouse && item.batch_no == this.selectedPackage
                  )
                : null
            if (!le) return null
            return this.fromFrontRoom ? Number(le.stock_value || 0) : Number(le.stock_value_back_room || 0)
        },
    },
    methods: {
        clampQty() {
            const max = this.availableMaxQty
            if (max !== null && Number(this.transferQty) > Number(max)) {
                this.transferQty = Number(max)
                this.$toast && this.$toast.error && this.$toast.error(`Max quantity allowed is ${max}`)
            }
            if (Number(this.transferQty) < 1) this.transferQty = 1
        },
        searchProductCondition(item, prodId = "") {
            return (
                item.id == (prodId || this.selectedProduct.id) &&
                item.selectedWarehouse.id == this.selectedWarehouse &&
                item.selectedRoom.id == this.selectedRoom &&
                item.selectedPackage.id == this.selectedPackage
            )
        },
        addProduct() {
            if (!this.selectedProduct || !this.selectedProduct.id) {
                return this.$toast.error("Please select a product to add!")
            }

            if (!this.onlyProductInput && !this.selectedPackage) {
                return this.$toast.error("Please select all fields to add product!")
            }
            if (!this.transferQty || Number(this.transferQty) <= 0) {
                return this.$toast.error("Please enter a valid quantity!")
            }
            if (this.availableMaxQty !== null && Number(this.transferQty) > Number(this.availableMaxQty)) {
                return this.$toast.error(`Max quantity allowed is ${this.availableMaxQty}`)
            }

            const isNewProduct = this.onlyProductInput
                ? !this.addedProducts.some((item) => item && String(item.id) == String(this.selectedProduct.id))
                : !this.addedProducts.find((item) => this.searchProductCondition(item))

            if (isNewProduct && this.onlyProductInput) {
                this.$emit("add", { ...this.selectedProduct })
                this.selectedProduct = null
                this.$refs.searchInput.clear()
                this.transferQty = 1
                this.$toast.success("Product added!")
                return
            }

            const ledgerEntry = this.selectedProduct.ledgerEntries.find(
                (item) => item && item.warehouse == this.selectedWarehouse && item.batch_no == this.selectedPackage
            )

            if (isNewProduct) {
                const fromFrontRoom = this.fromFrontRoom
                const stockQty = ledgerEntry
                    ? fromFrontRoom
                        ? Number(ledgerEntry.stock_value)
                        : Number(ledgerEntry.stock_value_back_room)
                    : 0

                this.$emit("add", {
                    ...this.selectedProduct,
                    selectedLedgerEntry: ledgerEntry,
                    selectedWarehouse: this.filteredWarehouses.find((item) => item.id == this.selectedWarehouse),
                    selectedRoom: this.selectedRoomData,
                    selectedPackage: this.filteredPackages.find((item) => item.id == this.selectedPackage),
                    fromFrontRoom,
                    stockQty,
                    adjustQty: Number(this.transferQty),
                    newQty: Number(this.transferQty),
                })
                this.selectedProduct = null
                this.$refs.searchInput.clear()
                this.transferQty = 1
                this.$toast.success("Product added!")
            } else {
                this.$toast.error("Product already added!")
            }
        },
    },
    props: {
        addedProducts: { type: Array, default: () => [] },
        onlyProductInput: { type: Boolean, default: false },
        defaultProductId: { type: String, default: "" },
        // When provided, these lock the location/room to the parent context
        defaultWarehouseId: { type: [String, Number], default: "" },
        lockWarehouse: { type: Boolean, default: false },
        defaultRoomId: { type: [String, Number], default: "" },
        lockRoom: { type: Boolean, default: false },
    },
    watch: {
        // reset values of child when parent changes
        selectedProduct(e) {
            // this.selectedWarehouse = null
            // this.selectedRoom = null
            this.selectedPackage = null
        },
        selectedWarehouse(e) {
            // this.selectedRoom = null
            this.selectedPackage = null
        },
        selectedRoom(e) {
            this.selectedPackage = null
        },
    },
    mounted() {
        // Preselect locked location/room
        if (this.lockWarehouse && this.defaultWarehouseId) this.selectedWarehouse = this.defaultWarehouseId
        if (this.lockRoom && this.defaultRoomId) this.selectedRoom = this.defaultRoomId
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

/* Main Container - Compact card with clear borders */
.product-adder-container {
    background: white;
    border: 2px solid $lightBorderColor;
    border-radius: 6px;
    padding: 16px;
    margin-bottom: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

/* Single Row Layout - All fields horizontal */
.fields-row {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: nowrap;
}

/* Field Group Styling */
.field-group {
    display: flex;
    flex-direction: column;
    min-width: 160px;
    flex: 1;
}

/* Product field takes more space */
.field-group-product {
    flex: 2;
    min-width: 240px;
}

/* Package field slightly wider */
.field-group-package {
    flex: 1.2;
    min-width: 180px;
}

.field-group-qty {
    flex: 0 0 120px;
    min-width: 120px;
}

/* Action button fixed width */
.field-group-action {
    flex: 0 0 auto;
    min-width: 100px;
}

/* Field Labels - Compact and clear */
.field-label {
    font-size: 12px;
    font-weight: 600;
    color: $textColor;
    margin-bottom: 6px;
    display: block;
    white-space: nowrap;
}

/* Empty State Messages */
.field-empty-state {
    font-size: 12px;
    color: $textColor;
    opacity: 0.6;
    padding: 8px 10px;
    background: $lightHoverColor;
    border-radius: 4px;
    border: 1px solid $lightBorderColor;
    height: 40px;
    display: flex;
    align-items: center;
}

/* Add Product Button Styling */
.add-product-btn {
    width: 100%;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-weight: 600;
    white-space: nowrap;
}

.add-product-btn i {
    font-size: 14px;
}

/* Dropdown Customization - Compact height */
:deep(.multiselect) { 
    min-width: 100%;
}

:deep(.multiselect__tags) {
    min-height: 40px;
    padding: 8px 40px 0 8px;
}

:deep(.multiselect__content-wrapper) {
    max-height: 300px;
}

:deep(.multiselect__single) {
    font-size: 13px;
    margin-bottom: 8px;
}

/* Ensure the product search input maintains consistent height */
.field-group-product :deep(.product-search-input-form) {
    height: 40px;
}

.field-group-product :deep(.suggestion-container) {
    min-width: 400px;
}

.qty-input {
    height: 40px;
    width: 100%;
    padding: 8px 10px;
    border: 1px solid $lightBorderColor;
    border-radius: 6px;
}
.qty-hint { font-size: 11px; color: rgba($textColor, 0.6); margin-top: 4px; }
.field-group-qty { position: relative; }
.field-group-qty .qty-hint { position: absolute; top: 100%; left: 0; margin-top: 2px; opacity: 0.7; }

/* Dark Mode Overrides */
.dark .product-adder-container {
    background: $darkCardBg;
    border-color: $darkBorderColor;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.dark .field-label {
    color: $darkTextColor;
}

.dark .field-empty-state {
    color: $darkTextColor;
    background: $darkInputBg;
    border-color: $darkBorderColor;
}

/* Responsive Design - Stack on smaller screens */
@media (max-width: 1200px) {
    .fields-row {
        flex-wrap: wrap;
    }
    
    .field-group-product {
        flex: 1 1 100%;
        min-width: 100%;
    }
}

@media (max-width: 768px) {
    .fields-row {
        flex-direction: column;
        align-items: stretch;
    }
    
    .field-group,
    .field-group-package,
    .field-group-action {
        min-width: 100%;
        width: 100%;
        flex: 1;
    }
}
</style>
